import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ApiCall from "../../../Apicall/ApiCall";
import { useSignalR } from "../../../context/SignalRContext";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import DealPaymentStage from "./payment/DealPaymentStage";
import { StagePlaceholder } from "./payment/PaymentParts";
import { useTranslation } from "react-i18next";
import { DEAL_HOME_PATH } from "../../lib/dealSession";

// The payment stage on a page of its own — where both parties land straight after signing, and
// where the overview's "View the deal" brings them back while the payment is unconfirmed.
//
// Deliberately NOT the signed agreement: the contract document and its download stay on
// /Contract/:id, reached from the transaction record once there is a record to read. At this
// stage the two parties are settling the money and nothing else, which is what the mockup's
// post-signing screens show — the identity band, the stage and the one thing to do.
//
// The deal-flow rules hold here as on every other deal page: the shared header carries the
// session countdown, leaving asks for confirmation, and the counterpart is told if this user
// signs out (see DealSessionManager, which works from the connected deal id joined below).
export default function DealPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: id },
        });
        const found = res?.data?.result || res?.result || null;
        if (!cancelled) {
          setDeal(found);
          setLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setDeal(null);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Only a signed, uncancelled deal has a payment stage at all. Anything else belongs on the
  // overview, which states where the deal actually stands instead of showing an empty page.
  useEffect(() => {
    if (loading) return;
    if (!deal || !deal.dealComplete || deal.dealCancel) {
      navigate(DEAL_HOME_PATH, { replace: true });
    }
  }, [loading, deal, navigate]);

  // Stay in the deal's group while this page is open, so the counterpart's sign-out reaches this
  // tab and the "Leave the deal?" guard applies here too. A party may join their own deal's
  // group whatever state it is in (NotificationHub.JoinDealGroup), so a signed deal is fine.
  const { dealId: connectedDealId, connectToDeal } = useSignalR();
  const joiningDeal = useRef(null);
  useEffect(() => {
    if (!id || !deal?.dealComplete || deal?.dealCancel) return;
    if (String(connectedDealId) === String(id) || joiningDeal.current === id) return;
    joiningDeal.current = id;
    connectToDeal(id)
      .catch(() => {
        /* the payment stage still works; only the live deal-session rules are missed */
      })
      .finally(() => {
        joiningDeal.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, deal, connectedDealId]);

  return (
    <PublicPage>
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          <div className="ds-form mx-auto w-full">
            {loading ? (
              // The stage says nothing until it knows where the deal stands.
              <StagePlaceholder label={t("payment.preparing")} />
            ) : (
              <DealPaymentStage contractId={id} onAdvance={navigate} />
            )}
          </div>
        </main>
      </div>
    </PublicPage>
  );
}
